import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Star, Crown, Sparkles, Users, GraduationCap, ShieldCheck, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import PromoBanner from '@/components/layout/PromoBanner';

// ── Pricing matrix — mirrors `backend/services/subscription_tiers.py:PRICING`.
// If those two ever drift, the test suite (`test_pricing_matrix_matches_spec`)
// will catch it on the backend side; this UI layer is the consumer.
const PRICING = {
  open_play: { monthly: 0, annual: 0 },
  verified_player: { monthly: 0.99, annual: 9.99 },
  instructor_starter: { monthly: 0, annual: 0 },
  instructor_plus: { monthly: 9.99, annual: 99.99 },
  instructor_unlimited: { monthly: 29.99, annual: 299.99 },
};

const ANNUAL_SAVINGS = (tier) => {
  const m = PRICING[tier].monthly;
  const a = PRICING[tier].annual;
  if (!m || !a) return 0;
  return Math.round(((m * 12 - a) / (m * 12)) * 100);
};

// ── Plan content — single source of truth for what each card shows.
// Kept as a plain data array so a designer can tweak copy without
// chasing JSX.
const PLAYER_PLANS = [
  {
    id: 'open_play',
    name: 'Open Play',
    tagline: 'Free Player Membership',
    description:
      'For players who want to explore Mahjong events, instructors, places to play, and community conversations.',
    icon: Users,
    accent: 'slate',
    features: [
      'Create and edit your full profile',
      'Save your favorite events to find them again later',
      'Post events as needed',
      'Free-to-attend events: $0.99',
      'Paid events: $9.99',
      'Search nearby play locations',
      'Discussion Board posting and access',
    ],
    cta: 'Sign Up Free',
    badge: null,
  },
  {
    id: 'verified_player',
    name: 'Verified Player',
    tagline: '',
    description:
      'For players who want to connect, message, and get notified about Mahjong near them.',
    icon: Sparkles,
    accent: 'teal',
    inheritsFrom: 'Open Play',
    trustNote:
      'The small fee verifies you’re a real person — keeping the community safe from spam and bad actors — and helps cover the cost of message notifications and event alerts sent by email and SMS.',
    features: [
      'Search and connect with other players',
      'In-site messaging — find and message players near you to set up games or coordinate at upcoming events',
      'Email and optional SMS alerts — get notified when someone messages you AND when new events pop up near you',
      'Request games with 1–3 players',
    ],
    cta: 'Subscribe',
    badge: 'Most Popular',
  },
];

const INSTRUCTOR_PLANS = [
  {
    id: 'instructor_starter',
    name: 'Instructor Starter',
    tagline: 'Free',
    description:
      'For instructors who want to be listed and share events as needed.',
    icon: GraduationCap,
    accent: 'slate',
    features: [
      'Create and edit your instructor profile and player profile',
      'Save your favorite events to find them again later',
      'Post events as needed',
      'Free-to-attend events: $0.99',
      'Paid events: $9.99',
      'Search nearby play locations',
      'Discussion Board posting and access',
    ],
    cta: 'Sign Up Free',
    badge: null,
  },
  {
    id: 'instructor_plus',
    name: 'Instructor Advantage',
    tagline: '',
    description:
      'For instructors who want to connect with players and post occasional events at a lower cost.',
    icon: Sparkles,
    accent: 'teal',
    inheritsFrom: 'Instructor Starter',
    features: [
      'Search and connect with other players',
      'In-site messaging — find and message players near you to set up games or coordinate at upcoming events',
      'Email and optional SMS alerts — get notified when someone messages you AND when new events pop up near you',
      'Request games with 1–3 players',
      'Free-to-attend events are free to post',
      'Paid events discounted to $6.99 per event',
    ],
    cta: 'Subscribe',
    badge: 'Most Popular',
  },
  {
    id: 'instructor_unlimited',
    name: 'Instructor Unlimited',
    tagline: '',
    description:
      'For instructors and hosts who post events regularly.',
    icon: Crown,
    accent: 'gold',
    inheritsFrom: 'Instructor Advantage',
    features: [
      'Unlimited event posting',
      'Free-to-attend events are free to post',
      'Paid events are included',
      'Premium Instructor badge displayed next to your name',
      'Best value for instructors posting 3+ paid events per month',
    ],
    cta: 'Subscribe',
    badge: 'Best Value',
  },
];

const ACCENT_STYLES = {
  slate: {
    card: 'border-slate-200 bg-white',
    iconBg: 'bg-slate-100 text-slate-600',
    cta: 'border border-slate-300 text-slate-700 hover:bg-slate-50 bg-white',
    badge: '',
    check: 'text-slate-500',
  },
  teal: {
    card: 'border-2 border-[#56dfb4] bg-white shadow-sm',
    iconBg: 'bg-[#56dfb4]/15 text-[#0f766e]',
    cta: 'bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]',
    badge: 'bg-[#56dfb4] text-slate-900',
    check: 'text-[#0f766e]',
  },
  gold: {
    card: 'border-2 border-amber-300 bg-gradient-to-br from-amber-50/60 to-white shadow-md',
    iconBg: 'bg-amber-100 text-amber-700',
    cta: 'bg-amber-500 text-white hover:bg-amber-600',
    badge: 'bg-amber-400 text-slate-900',
    check: 'text-amber-600',
  },
};

function priceLabel(tier, cadence) {
  const p = PRICING[tier];
  if (!p) return null;
  if (p.monthly === 0 && p.annual === 0) return { headline: 'Free', sub: 'forever' };
  const amount = cadence === 'annual' ? p.annual : p.monthly;
  return {
    headline: `$${amount.toFixed(2)}`,
    sub: cadence === 'annual' ? '/year' : '/month',
  };
}

function PlanCard({ plan, cadence }) {
  const Icon = plan.icon;
  const styles = ACCENT_STYLES[plan.accent];
  const price = priceLabel(plan.id, cadence);
  const savings = cadence === 'annual' ? ANNUAL_SAVINGS(plan.id) : 0;
  const isComingSoon = plan.cta === 'Coming Soon';

  return (
    <div
      data-testid={`pricing-card-${plan.id}`}
      className={`relative p-7 rounded-3xl ${styles.card} flex flex-col`}
    >
      {plan.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span
            className={`inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-semibold ${styles.badge}`}
            data-testid={`pricing-badge-${plan.id}`}
          >
            <Star className="w-3 h-3" /> {plan.badge}
          </span>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4 mt-1">
        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${styles.iconBg}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-xl font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {plan.name}
          </h3>
          {plan.tagline && (
            <p className="text-xs text-slate-500 mt-0.5">{plan.tagline}</p>
          )}
        </div>
      </div>

      <p className="text-sm text-slate-600 mb-5 min-h-[3rem]">{plan.description}</p>

      {plan.trustNote && (
        <div
          className="flex gap-2 items-start mb-5 p-3 rounded-xl bg-[#56dfb4]/10 border border-[#56dfb4]/30"
          data-testid={`pricing-trust-note-${plan.id}`}
        >
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#0f766e]" />
          <p className="text-xs text-slate-700 leading-relaxed">{plan.trustNote}</p>
        </div>
      )}

      <div className="mb-5">
        <div className="flex items-baseline gap-1.5">
          <span className="text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {price.headline}
          </span>
          <span className="text-sm text-slate-500">{price.sub}</span>
        </div>
        {savings > 0 && (
          <p className="text-xs text-[#0f766e] font-medium mt-1" data-testid={`pricing-savings-${plan.id}`}>
            Save {savings}% vs monthly
          </p>
        )}
      </div>

      {plan.inheritsFrom && (
        <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">
          Includes everything in {plan.inheritsFrom}, plus:
        </p>
      )}

      <ul className="space-y-2.5 mb-6 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${styles.check}`} />
            <span className="text-slate-700">{f}</span>
          </li>
        ))}
      </ul>

      <Button
        asChild={!isComingSoon}
        disabled={isComingSoon}
        className={`w-full rounded-full py-3 text-sm font-medium ${styles.cta} ${isComingSoon ? 'opacity-60 cursor-not-allowed' : ''}`}
        data-testid={`pricing-cta-${plan.id}`}
      >
        {isComingSoon ? (
          <span>{plan.cta}</span>
        ) : (
          // Deep-link to /register with tier+cadence; the SubscriptionPage
          // reads these query params and pre-selects the right plan after
          // signup completes (or right away for logged-in users).
          <Link to={`/register?tier=${plan.id}&cadence=${cadence}`}>{plan.cta}</Link>
        )}
      </Button>
    </div>
  );
}

function CadenceToggle({ value, onChange, location = 'top' }) {
  return (
    <div className="inline-flex items-center bg-slate-100 rounded-full p-1" data-testid={`pricing-cadence-toggle-${location}`}>
      {[
        { id: 'monthly', label: 'Monthly' },
        { id: 'annual', label: 'Annual' },
      ].map((opt) => (
        <button
          key={opt.id}
          onClick={() => onChange(opt.id)}
          data-testid={`pricing-cadence-${opt.id}-${location}`}
          className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${
            value === opt.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {opt.label}
          {opt.id === 'annual' && <span className="ml-1.5 text-[#0f766e] text-xs">save up to 16%</span>}
        </button>
      ))}
    </div>
  );
}

// JSON-LD structured data: 3 paid tiers expressed as a `Product` with
// 6 `Offer`s (monthly + annual for each tier). Google can use this to
// surface "$0.99/mo" / "From $0.99" rich snippets on SERPs for queries
// like "American Mahjong instructor membership pricing". The free tiers
// are documented in the page copy but intentionally omitted from
// structured data — schema.org `Offer` requires a `price`, and Google's
// crawler treats $0 offers as misconfigured ads.
//
// `image` is required by Google's Merchant Listings spec — without it,
// Search Console raises "Missing field 'image'" warnings. We point at
// the same brand logo used for og:image so SERPs surface a recognizable
// thumbnail next to the pricing rich snippet.
const PRICING_PRODUCT_IMAGE = 'https://meetformahjong.com/logo.jpg';

const PRICING_JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Product',
      '@id': 'https://meetformahjong.com/pricing#verified-player',
      name: 'Verified Player Membership — Meet For Mahjong',
      image: PRICING_PRODUCT_IMAGE,
      description: 'Search and connect with verified American Mahjong players, in-site messaging to find and message players near you, email and SMS alerts for both new messages and events popping up nearby, request games with 1–3 players.',
      brand: { '@type': 'Brand', name: 'Meet For Mahjong' },
      offers: [
        { '@type': 'Offer', name: 'Verified Player — Monthly', price: '0.99', priceCurrency: 'USD', availability: 'https://schema.org/InStock', category: 'Subscription', url: 'https://meetformahjong.com/register?tier=verified_player&cadence=monthly' },
        { '@type': 'Offer', name: 'Verified Player — Annual', price: '9.99', priceCurrency: 'USD', availability: 'https://schema.org/InStock', category: 'Subscription', url: 'https://meetformahjong.com/register?tier=verified_player&cadence=annual' },
      ],
    },
    {
      '@type': 'Product',
      '@id': 'https://meetformahjong.com/pricing#instructor-advantage',
      name: 'Instructor Advantage Membership — Meet For Mahjong',
      image: PRICING_PRODUCT_IMAGE,
      description: 'For Mahjong instructors who want to connect with players and post occasional events at a lower cost. Includes in-site messaging to find and message players near you, plus email and SMS alerts for both new messages and events popping up nearby. Free-to-attend events are free to post; paid events at $6.99.',
      brand: { '@type': 'Brand', name: 'Meet For Mahjong' },
      offers: [
        { '@type': 'Offer', name: 'Instructor Advantage — Monthly', price: '9.99', priceCurrency: 'USD', availability: 'https://schema.org/InStock', category: 'Subscription', url: 'https://meetformahjong.com/register?tier=instructor_plus&cadence=monthly' },
        { '@type': 'Offer', name: 'Instructor Advantage — Annual', price: '99.99', priceCurrency: 'USD', availability: 'https://schema.org/InStock', category: 'Subscription', url: 'https://meetformahjong.com/register?tier=instructor_plus&cadence=annual' },
      ],
    },
    {
      '@type': 'Product',
      '@id': 'https://meetformahjong.com/pricing#instructor-unlimited',
      name: 'Instructor Unlimited Membership — Meet For Mahjong',
      image: PRICING_PRODUCT_IMAGE,
      description: 'For instructors and hosts who post events regularly. Unlimited free and paid event posting, Premium Instructor badge, priority placement.',
      brand: { '@type': 'Brand', name: 'Meet For Mahjong' },
      offers: [
        { '@type': 'Offer', name: 'Instructor Unlimited — Monthly', price: '29.99', priceCurrency: 'USD', availability: 'https://schema.org/InStock', category: 'Subscription', url: 'https://meetformahjong.com/register?tier=instructor_unlimited&cadence=monthly' },
        { '@type': 'Offer', name: 'Instructor Unlimited — Annual', price: '299.99', priceCurrency: 'USD', availability: 'https://schema.org/InStock', category: 'Subscription', url: 'https://meetformahjong.com/register?tier=instructor_unlimited&cadence=annual' },
      ],
    },
  ],
};

export default function PricingPage() {
  const [cadence, setCadence] = useState('monthly');
  // Persona toggle — 'both' is default so first-time visitors and SEO
  // crawlers see the full grid. Clicking "I'm a player" or "I'm an
  // instructor" dims the irrelevant section without removing it (Notion /
  // Linear style). Reduces decision fatigue on the public pricing page.
  // Hydrated from localStorage on mount so returning visitors see their
  // preferred view immediately.
  const [persona, setPersona] = useState('both');
  useEffect(() => {
    try {
      const saved = localStorage.getItem('mfm_pricing_persona');
      if (saved === 'player' || saved === 'instructor' || saved === 'both') {
        setPersona(saved);
      }
    } catch (_e) { /* localStorage blocked — silently fall back to 'both' */ }
  }, []);
  const updatePersona = (next) => {
    setPersona(next);
    try { localStorage.setItem('mfm_pricing_persona', next); } catch (_e) { /* noop */ }
  };
  const playerDimmed = persona === 'instructor';
  const instructorDimmed = persona === 'player';

  return (
    <div data-testid="pricing-page">
      <SEO
        title="Membership — Player &amp; Instructor Plans"
        description="Two free and three paid tiers for American Mahjong players and instructors. Open Play, Verified Player, Instructor Starter, Plus, and Unlimited."
        path="/pricing"
        jsonLd={PRICING_JSON_LD}
      />
      <PromoBanner />
      <div className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#b456df]">Membership</span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Membership built for players <span className="text-[#b456df]">and</span> instructors.
            </h1>
            <p className="text-slate-500 text-base sm:text-lg max-w-2xl mx-auto">
              Pick the path that fits how you play, host, and teach. Upgrade or downgrade any time.
            </p>
          </div>

          {/* Persona toggle — softens the visual hierarchy for visitors
              who already know whether they want to play vs teach. */}
          <div className="flex justify-center mb-6" data-testid="pricing-persona-toggle">
            <div className="inline-flex bg-slate-100 p-1 rounded-full">
              <button
                type="button"
                onClick={() => updatePersona('both')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${persona === 'both' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="pricing-persona-both"
              >
                Show all
              </button>
              <button
                type="button"
                onClick={() => updatePersona('player')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${persona === 'player' ? 'bg-white shadow-sm text-[#b456df]' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="pricing-persona-player"
              >
                I'm a player
              </button>
              <button
                type="button"
                onClick={() => updatePersona('instructor')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${persona === 'instructor' ? 'bg-white shadow-sm text-[#df5681]' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="pricing-persona-instructor"
              >
                I'm an instructor
              </button>
            </div>
          </div>

          {/* Cadence toggle */}
          <div className="flex justify-center mb-12">
            <CadenceToggle value={cadence} onChange={setCadence} />
          </div>

          {/* Player tiers */}
          <section
            className={`mb-16 transition-opacity duration-300 ${playerDimmed ? 'hidden' : 'opacity-100'}`}
            data-testid="pricing-player-section"
            data-dimmed={playerDimmed ? 'true' : 'false'}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-2xl bg-[#b456df]/10 text-[#b456df] flex items-center justify-center">
                <Users className="w-4.5 h-4.5" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                For Players
              </h2>
              <span className="hidden sm:inline-block text-xs text-slate-400 ml-2">
                Two ways to play
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {PLAYER_PLANS.map((p) => (
                <PlanCard key={p.id} plan={p} cadence={cadence} />
              ))}
            </div>
          </section>

          {/* Instructor tiers */}
          <section
            data-testid="pricing-instructor-section"
            className={`transition-opacity duration-300 ${instructorDimmed ? 'hidden' : 'opacity-100'}`}
            data-dimmed={instructorDimmed ? 'true' : 'false'}
          >
            {/* Inline cadence toggle — only useful when both sections are
                visible, since scrolling past the player section may take
                the top-of-page toggle out of view. Hidden in single-persona
                modes where the top toggle is right above this section. */}
            {persona === 'both' && (
              <div className="flex justify-center mb-8">
                <CadenceToggle value={cadence} onChange={setCadence} location="instructor" />
              </div>
            )}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-2xl bg-[#df5681]/10 text-[#df5681] flex items-center justify-center">
                <GraduationCap className="w-4.5 h-4.5" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                For Instructors
              </h2>
              <span className="hidden sm:inline-block text-xs text-slate-400 ml-2">
                Three levels for hosts and teachers
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {INSTRUCTOR_PLANS.map((p) => (
                <PlanCard key={p.id} plan={p} cadence={cadence} />
              ))}
            </div>

            {/* New-instructor onboarding offer — Instructor Starter & Advantage only */}
            <div
              data-testid="pricing-instructor-onboarding-offer"
              className="mt-8 p-5 sm:p-6 rounded-3xl border-2 border-dashed border-[#df5681]/30 bg-[#df5681]/5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <div className="w-11 h-11 rounded-2xl bg-[#df5681]/15 text-[#df5681] flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  New to teaching or hosting with Meet For Mahjong?
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Instructors on <strong>Instructor Starter</strong> or <strong>Instructor Advantage</strong> can post
                  their <strong>first six events for free</strong>. Reach out and we’ll set you up.
                </p>
              </div>
              <Link to="/contact" data-testid="pricing-instructor-onboarding-cta" className="flex-shrink-0">
                <Button className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] px-5">
                  Contact us
                </Button>
              </Link>
            </div>
          </section>

          {/* Footnote */}
          <p
            className="text-xs text-slate-400 text-center mt-12 max-w-2xl mx-auto"
            data-testid="pricing-footnote"
          >
            Prices in USD. Cancel anytime. Members on legacy monthly, annual, or lifetime plans
            will be transitioned automatically with no loss of benefits.
          </p>
        </div>
      </div>
    </div>
  );
}
